import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import useEmblaCarousel from 'embla-carousel-react';

interface ItineraryDay {
  day: string;
  activities: string[];
}

interface ItineraryMedia {
  type: 'image' | 'video';
  url: string;
  thumbnail?: string;
}

interface ItineraryCarouselProps {
  itinerary: ItineraryDay[];
  media: ItineraryMedia[];
  className?: string;
}

export function ItineraryCarousel({ itinerary, media, className = '' }: ItineraryCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [currentIndex, setCurrentIndex] = useState(0);

  const scrollPrev = () => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      setCurrentIndex(emblaApi.selectedScrollSnap());
    }
  };

  const scrollNext = () => {
    if (emblaApi) {
      emblaApi.scrollNext();
      setCurrentIndex(emblaApi.selectedScrollSnap());
    }
  };

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', () => {
        setCurrentIndex(emblaApi.selectedScrollSnap());
      });
    }
  }, [emblaApi]);

  return (
    <div className={`relative ${className}`}>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {itinerary.map((day, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 p-4">
                {/* Media Section - 8 columns on desktop */}
                <div className="md:col-span-8">
                  <div className="aspect-video rounded-lg overflow-hidden">
                    {media[index % media.length]?.type === 'video' ? (
                      <video
                        src={media[index % media.length].url}
                        poster={media[index % media.length].thumbnail}
                        controls
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={media[index % media.length].url}
                        alt={`Day ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                </div>

                {/* Itinerary Section - 4 columns on desktop */}
                <div className="md:col-span-4">
                  <h3 className="text-xl font-display text-sand-100 mb-4">{day.day}</h3>
                  <ul className="list-disc list-inside text-sand-300 space-y-2">
                    {day.activities.map((activity, actIndex) => (
                      <li key={actIndex} className="pl-4 -indent-4">{activity}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons - Moved even further to edges */}
      <Button
        variant="secondary"
        size="sm"
        onClick={scrollPrev}
        className="absolute -left-7 sm:-left-8 top-1/2 -translate-y-1/2 z-10 bg-earth-900/80 hover:bg-earth-900 backdrop-blur-sm w-8 h-8 sm:w-10 sm:h-10 rounded-full p-0"
      >
        <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6" />
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={scrollNext}
        className="absolute -right-7 sm:-right-8 top-1/2 -translate-y-1/2 z-10 bg-earth-900/80 hover:bg-earth-900 backdrop-blur-sm w-8 h-8 sm:w-10 sm:h-10 rounded-full p-0"
      >
        <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6" />
      </Button>

      {/* Progress Indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
        {itinerary.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentIndex ? 'bg-sand-300' : 'bg-earth-700'
            }`}
          />
        ))}
      </div>
    </div>
  );
}